import React, { useState, useRef, useEffect } from "react";
import { AdditionalPhones, parsePhones, withCountryCode } from "../utils/phones";
import { Link, useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import Swal from "sweetalert2";
import { Pagination } from "@mui/material";
import EnquiryModal from "./enquiryModal";
import TableLoader from "../../Loader/TableLoader";
import { useDoneLeads, useDeleteEnquiry } from "../hooks/useManageEnquiry";
import { useFollowUpOptions } from "../hooks/useManageEnquiry";
import usePermissions from "../hooks/usePermissions";

import { Button, Input, Tooltip, Popover } from "antd";
import {
  FilterOutlined,
  ClearOutlined,
  SearchOutlined,
  CalendarOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";

const CustomDateInput = React.forwardRef(
  ({ value, onClick, placeholder }, ref) => (
    <div
      onClick={onClick}
      ref={ref}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "8px 12px",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        cursor: "pointer",
        minHeight: "42px",
        background: "white",
        width: "100%",
        fontSize: "14px",
        color: value ? "#262626" : "#bfbfbf",
      }}
    >
      <CalendarOutlined
        style={{ color: "#16a34a", fontSize: "14px", flexShrink: 0 }}
      />
      <span style={{ flex: 1 }}>{value || placeholder}</span>
    </div>
  ),
);

const EMPTY_FILTER = { search: "", start_date: "", end_date: "" };

/* Kanban buckets. Mutually exclusive by design: "Expiring Soon" wins over the
   plain active columns, otherwise a lead would be counted twice and the column
   totals would never add up to the header count.
   The backend will send `bucket` per row — when it does, that value wins and
   this classifier is only the fallback. */
const EXPIRING_DAYS = 7;

const DONE_COLUMNS = [
  { key: "free_trial",         label: "Free Trial",         color: "#2563eb", border: "#bfdbfe", bg: "#eff6ff" },
  { key: "expiring_soon",      label: "Expiring Soon",      color: "#d97706", border: "#fde68a", bg: "#fffbeb" },
  { key: "paid_active",        label: "Paid Active",        color: "#16a34a", border: "#bbf7d0", bg: "#f0fdf4" },
  { key: "free_trial_expired", label: "Free Trial Expired", color: "#6b7280", border: "#e5e7eb", bg: "#f9fafb" },
  { key: "paid_expired",       label: "Paid Expired",       color: "#dc2626", border: "#fecaca", bg: "#fef2f2" },
  { key: "no_subscription",    label: "No Subscription",    color: "#9ca3af", border: "#e5e7eb", bg: "#f9fafb" },
];

const classifyConverted = (item) => {
  if (item?.bucket) return item.bucket;              // backend decides when it can
  const sub = item?.subscription;
  if (!sub) return "no_subscription";

  const isTrial = String(sub.plan_type || "").toLowerCase() === "free_trial";
  const end = sub.end_date ? moment(sub.end_date, "YYYY-MM-DD") : null;
  if (!end || !end.isValid()) return isTrial ? "free_trial" : "paid_active";

  const today = moment().startOf("day");
  if (end.isBefore(today, "day")) return isTrial ? "free_trial_expired" : "paid_expired";
  if (end.diff(today, "days") <= EXPIRING_DAYS) return "expiring_soon";
  return isTrial ? "free_trial" : "paid_active";
};

const DoneLeads = () => {
  const { canDelete } = usePermissions("/cms/manageenquiry/done");
  const navigate = useNavigate();

  const [pageApi, setPageApi] = useState(1);
  const [filterForm, setFilterForm] = useState(EMPTY_FILTER);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [appliedFilter, setAppliedFilter] = useState(EMPTY_FILTER);
  const [isFilterLoading, setIsFilterLoading] = useState(false);

  const [viewMode, setViewMode] = useState("kanban"); // "kanban" | "list"
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [showCheckboxes, setShowCheckboxes] = useState(false);
  const [isDeleteAll, setIsDeleteAll] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const [statusId, setStatusId] = useState(0);
  const [statusCode, setStatusCode] = useState(0);
  const [modalIsOpen2, setIsOpen2] = useState(false);
  const [modalStatus, setModalStatus] = useState(false);

  const searchDebounceRef = useRef(null);

  const {
    data: listData,
    isLoading,
    isFetching,
  } = useDoneLeads(pageApi, {
    ...appliedFilter,
    status: "7",
    // The board must show every converted lead, not just the current page.
    ...(viewMode === "kanban" ? { limit: 200 } : {}),
  });
  const deleteEnquiry = useDeleteEnquiry();
  const { data: followUpOptions = [] } = useFollowUpOptions();

  const followUpStatusMap = React.useMemo(() => {
    const map = {};
    followUpOptions.forEach((opt) => {
      map[String(opt.option_value)] = opt.label;
    });
    return map;
  }, [followUpOptions]);

  const listPageData = listData?.data ?? [];

  /* A converted lead IS a customer, so opening one goes to the customer profile
     (customer_id from the API is the user id). Legacy rows that never got a
     customer still fall back to the enquiry view. */
  const openConverted = (item) => {
    if (item?.customer_id) navigate(`/cms/manageUserData/user-profile/${item.customer_id}`);
    else navigate(`/cms/manageenquiry/view-enquiry/${item.id}`);
  };
  const totalUser = listData?.total ?? 0;

  /* Columns for the board. Only buckets that actually have leads are rendered,
     so a clean account does not stare at five empty columns. */
  const doneColumns = React.useMemo(() => {
    const buckets = Object.fromEntries(DONE_COLUMNS.map((c) => [c.key, []]));
    listPageData.forEach((item) => {
      const key = classifyConverted(item);
      if (buckets[key]) buckets[key].push(item);
    });
    /* Header count comes from the API when it sends one — it counts the whole
       filtered set, not just the rows on this page. */
    const counts = listData?.counts || null;
    return DONE_COLUMNS.map((c) => ({
      ...c,
      items: buckets[c.key],
      count: counts && counts[c.key] != null ? counts[c.key] : buckets[c.key].length,
    })).filter(
      (c) => c.key !== "no_subscription" || c.items.length > 0,
    );
  }, [listPageData, listData]);
  const totalPages = listData?.totalPages ?? 1;

  // Attorney No. column only appears when at least one lead on the page has one,
  // so the table doesn't carry a column full of dashes for non-attorney leads.
  const showAttorneyCol = listPageData.some((i) =>
    String(i?.attorney_number ?? "").trim(),
  );

  useEffect(() => {
    if (!isFetching) setIsFilterLoading(false);
  }, [isFetching]);

  const followUpStatusStyle = (label) => {
    const l = (label || "").toLowerCase();
    if (l === "follow up")
      return { bg: "#fffbeb", color: "#d97706", border: "#fde68a" };
    if (l === "hot lead")
      return { bg: "#fff7ed", color: "#ea580c", border: "#fed7aa" };
    if (l === "no response")
      return { bg: "#f9fafb", color: "#6b7280", border: "#e5e7eb" };
    if (l === "interested")
      return { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" };
    if (l === "mark for future")
      return { bg: "#fefce8", color: "#ca8a04", border: "#fef08a" };
    if (l === "not interested")
      return { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" };
    if (l === "meeting scheduled")
      return { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe" };
    if (l === "converted")
      return { bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0" };
    if (l === "not responding")
      return { bg: "#f5f5f5", color: "#9ca3af", border: "#e5e7eb" };
    if (l === "closed")
      return { bg: "#f5f3ff", color: "#7c3aed", border: "#ddd6fe" };
    if (l === "negative")
      return { bg: "#fef2f2", color: "#dc2626", border: "#fecaca" };
    return { bg: "#f9fafb", color: "#6b7280", border: "#e5e7eb" };
  };

  // console.log("from done-list", listData);

  /* ── filter helpers ── */
  const handleSearchChange = (e) => {
    const v = e.target.value;
    setFilterForm((p) => ({ ...p, search: v }));
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setPageApi(1);
      setAppliedFilter((p) => ({ ...p, search: v }));
    }, 500);
  };

  const applyFilter = () => {
    setPageApi(1);
    setIsFilterLoading(true);
    setAppliedFilter({
      ...filterForm,
      start_date: startDate
        ? moment(startDate).utc().add(1, "d").format("YYYY-MM-DD")
        : "",
      end_date: endDate
        ? moment(endDate).utc().add(1, "d").format("YYYY-MM-DD")
        : "",
    });
  };

  const clearFilter = () => {
    setFilterForm(EMPTY_FILTER);
    setStartDate("");
    setEndDate("");
    setAppliedFilter(EMPTY_FILTER);
    setPageApi(1);
  };

  /* ── checkbox helpers ── */
  const handleCheckAll = () => {
    const n = !selectAll;
    setSelectAll(n);
    setIsDeleteAll(n ? listPageData.map((i) => i.id) : []);
  };

  const handleCheckboxChange = (item) =>
    setIsDeleteAll((p) =>
      p.includes(item.id) ? p.filter((id) => id !== item.id) : [...p, item.id],
    );

  /* ── delete ── */
  const handleDeleteSingleRow = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Delete this lead?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    }).then((r) => {
      if (r.isConfirmed)
        deleteEnquiry.mutate(id, {
          onSuccess: () => Swal.fire("Deleted!", "Lead deleted.", "success"),
        });
    });
  };

  const showAlertDelete = () => {
    if (isDeleteAll.length === 0) {
      Swal.fire({ icon: "error", title: "No leads selected" });
      return;
    }
    Swal.fire({
      title: "Delete selected?",
      text: `Delete ${isDeleteAll.length} lead(s)?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    }).then((r) => {
      if (r.isConfirmed)
        deleteEnquiry.mutate(isDeleteAll, {
          onSuccess: () => {
            Swal.fire("Deleted!", "Deleted.", "success");
            setIsDeleteAll([]);
            setSelectAll(false);
            setShowCheckboxes(false);
          },
        });
    });
  };

  /* ── styles ── */
  const labelStyle = {
    fontWeight: 600,
    color: "#374151",
    fontSize: "13px",
    marginBottom: "6px",
    display: "flex",
    alignItems: "center",
    gap: "5px",
  };

  const statusBadge = (val) => {
    if (val == 1)
      return {
        bg: "#f0fdf4",
        color: "#16a34a",
        border: "#bbf7d0",
        label: "● Positive",
      };
    if (val == 2)
      return {
        bg: "#fef2f2",
        color: "#dc2626",
        border: "#fecaca",
        label: "● Negative",
      };
    if (val == 0)
      return {
        bg: "#eff6ff",
        color: "#2563eb",
        border: "#bfdbfe",
        label: "● Neutral",
      };
    return {
      bg: "#fffbeb",
      color: "#d97706",
      border: "#fde68a",
      label: "● Pending",
    };
  };

  const activeFiltersCount = [filterForm.search, startDate, endDate].filter(
    Boolean,
  ).length;

  return (
    <>
      <style>{`
        .custom-datepicker-wrapper{width:100%}
        .custom-datepicker-wrapper .react-datepicker-wrapper{width:100%}
        .custom-datepicker-wrapper .react-datepicker__input-container{width:100%}
        .custom-datepicker-wrapper .react-datepicker-popper{z-index:9999!important}
        .custom-datepicker-wrapper .react-datepicker{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;border:1px solid #bbf7d0;border-radius:12px;box-shadow:0 8px 24px rgba(22,163,74,.15);overflow:hidden;font-size:13px}
        .custom-datepicker-wrapper .react-datepicker__header{background:linear-gradient(135deg,#16a34a,#15803d);border-bottom:none;padding:10px 8px 6px}
        .custom-datepicker-wrapper .react-datepicker__current-month{color:#fff;font-weight:700;font-size:14px;margin-bottom:4px}
        .custom-datepicker-wrapper .react-datepicker__day-name{color:rgba(255,255,255,.85);font-weight:600;font-size:11px;width:32px;line-height:32px;margin:1px;text-align:center}
        .custom-datepicker-wrapper .react-datepicker__day{width:32px;line-height:32px;margin:1px;border-radius:6px;font-size:13px;color:#374151;text-align:center}
        .custom-datepicker-wrapper .react-datepicker__day:hover{background:#f0fdf4;color:#16a34a}
        .custom-datepicker-wrapper .react-datepicker__day--selected{background:linear-gradient(135deg,#16a34a,#15803d)!important;color:#fff!important;font-weight:600}
        .custom-datepicker-wrapper .react-datepicker__day--today{font-weight:700;color:#16a34a}
        .custom-datepicker-wrapper .react-datepicker__navigation-icon::before{border-color:rgba(255,255,255,.8);border-width:2px 2px 0 0;height:7px;width:7px}
        .custom-datepicker-wrapper .react-datepicker__triangle{display:none}
        @keyframes badgePop{0%{transform:scale(1)}50%{transform:scale(1.2)}100%{transform:scale(1)}}
        .sel-badge{animation:badgePop .25s ease}
      `}</style>

      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            {/* ══ PAGE HEADER ══ */}
            <div
              style={{
                background: "linear-gradient(135deg,#16a34a 0%,#15803d 100%)",
                borderRadius: "16px",
                padding: "28px 32px",
                marginBottom: "28px",
                boxShadow: "0 8px 32px rgba(22,163,74,.28)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "16px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "16px" }}
              >
                <div
                  style={{
                    width: "56px",
                    height: "56px",
                    background: "rgba(255,255,255,.22)",
                    borderRadius: "14px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "26px",
                    color: "white",
                  }}
                >
                  <CheckCircleOutlined />
                </div>
                <div>
                  <h4
                    style={{
                      color: "white",
                      margin: 0,
                      fontSize: "24px",
                      fontWeight: 700,
                    }}
                  >
                    Done / Converted
                  </h4>
                  <p
                    style={{
                      color: "rgba(255,255,255,.85)",
                      margin: 0,
                      fontSize: "14px",
                    }}
                  >
                    Successfully converted leads
                  </p>
                </div>
              </div>
              <div
                style={{
                  background: "rgba(255,255,255,.18)",
                  padding: "10px 18px",
                  borderRadius: "10px",
                  display: "flex",
                  gap: "6px",
                  alignItems: "center",
                  fontSize: "13px",
                  color: "white",
                }}
              >
                <Link
                  to="/cms/manageenquiry"
                  style={{ color: "white", textDecoration: "none" }}
                >
                  Manage Leads
                </Link>
                <span style={{ color: "rgba(255,255,255,.5)" }}>›</span>
                <span style={{ fontWeight: 600 }}>Done</span>
              </div>
            </div>

            {/* ══ FILTER CARD ══ */}
            <div
              style={{
                background: "white",
                borderRadius: "16px",
                boxShadow: "0 2px 16px rgba(0,0,0,.07)",
                border: "1px solid #f0f0f0",
                marginBottom: "24px",
                overflow: "visible",
              }}
            >
              <div
                onClick={() => setFiltersOpen((o) => !o)}
                style={{
                  background: "linear-gradient(135deg,#f0fdf4,#dcfce7)",
                  borderBottom: filtersOpen ? "2px solid #bbf7d0" : "none",
                  padding: "18px 24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <div
                    style={{
                      width: "38px",
                      height: "38px",
                      background: "linear-gradient(135deg,#16a34a,#15803d)",
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "16px",
                    }}
                  >
                    <FilterOutlined />
                  </div>
                  <div>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: "16px",
                        color: "#1f2937",
                      }}
                    >
                      Filters
                    </div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>
                      Filter converted leads
                    </div>
                  </div>
                </div>
                {activeFiltersCount > 0 && (
                  <span
                    style={{
                      background: "linear-gradient(135deg,#16a34a,#15803d)",
                      color: "white",
                      borderRadius: "20px",
                      padding: "4px 14px",
                      fontSize: "13px",
                      fontWeight: 600,
                    }}
                  >
                    {activeFiltersCount} active
                  </span>
                )}
                <span style={{ color: "#15803d", fontSize: "13px", marginLeft: "auto", paddingLeft: "12px" }}>
                  {filtersOpen ? "▲ Collapse" : "▼ Expand"}
                </span>
              </div>
              {filtersOpen && (
              <div style={{ padding: "24px" }}>
                <div className="row g-3">
                  <div className="col-md-4">
                    <div style={labelStyle}>
                      <SearchOutlined style={{ color: "#16a34a" }} /> Search
                    </div>
                    <Input
                      size="large"
                      placeholder="Name, email or mobile..."
                      value={filterForm.search}
                      onChange={handleSearchChange}
                      prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                      style={{ borderRadius: "8px" }}
                      allowClear
                    />
                  </div>
                  <div className="col-md-4">
                    <div style={labelStyle}>
                      <CalendarOutlined style={{ color: "#16a34a" }} />{" "}
                      Subscription Start Date
                    </div>
                    <div className="custom-datepicker-wrapper">
                      <DatePicker
                        selected={startDate}
                        onChange={(d) => setStartDate(d)}
                        selectsStart
                        startDate={startDate}
                        endDate={endDate}
                        dateFormat="dd MMM yyyy"
                        placeholderText="Subscription start"
                        isClearable={!!startDate}
                        customInput={
                          <CustomDateInput placeholder="Subscription start" />
                        }
                        popperPlacement="bottom-start"
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div style={labelStyle}>
                      <CalendarOutlined style={{ color: "#16a34a" }} />{" "}
                      Subscription End Date
                    </div>
                    <div className="custom-datepicker-wrapper">
                      <DatePicker
                        selected={endDate}
                        onChange={(d) => setEndDate(d)}
                        selectsEnd
                        startDate={startDate}
                        endDate={endDate}
                        minDate={startDate}
                        dateFormat="dd MMM yyyy"
                        placeholderText="Subscription end"
                        isClearable={!!endDate}
                        customInput={<CustomDateInput placeholder="Subscription end" />}
                        popperPlacement="bottom-start"
                      />
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: "20px",
                    paddingTop: "20px",
                    borderTop: "1px solid #f0f0f0",
                  }}
                >
                  <Button
                    size="large"
                    icon={<ClearOutlined />}
                    onClick={clearFilter}
                    style={{
                      borderRadius: "10px",
                      minWidth: "130px",
                      fontWeight: 600,
                      borderColor: "#16a34a",
                      color: "#16a34a",
                      height: "44px",
                    }}
                  >
                    Clear
                  </Button>
                  <Button
                    type="primary"
                    size="large"
                    icon={<FilterOutlined />}
                    onClick={applyFilter}
                    loading={isFilterLoading}
                    style={{
                      borderRadius: "10px",
                      minWidth: "130px",
                      fontWeight: 600,
                      height: "44px",
                      background: "linear-gradient(135deg,#16a34a,#15803d)",
                      border: "none",
                      boxShadow: "0 4px 12px rgba(22,163,74,.4)",
                    }}
                  >
                    Apply
                  </Button>
                </div>
              </div>
              )}
            </div>

            <EnquiryModal
              token={localStorage.getItem("cms_token")}
              modalIsOpen2={modalIsOpen2}
              setIsOpen2={setIsOpen2}
              id={statusId}
              setStatusId={setStatusId}
              statusCode={statusCode}
              setStatusCode={setStatusCode}
              setModalStatus={setModalStatus}
              modalStatus={modalStatus}
            />

            {/* ══ TABLE CARD ══ */}
            <div
              style={{
                background: "white",
                borderRadius: "16px",
                boxShadow: "0 2px 16px rgba(0,0,0,.07)",
                border: "1px solid #f0f0f0",
                overflow: "hidden",
              }}
            >
              {/* Action bar */}
              <div
                style={{
                  padding: "20px 24px",
                  borderBottom: "2px solid #f5f5f5",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <div
                    style={{
                      width: "38px",
                      height: "38px",
                      background: "linear-gradient(135deg,#16a34a,#15803d)",
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "16px",
                    }}
                  >
                    <CheckCircleOutlined />
                  </div>
                  <div>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: "17px",
                        color: "#1f2937",
                      }}
                    >
                      Converted Leads
                    </div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>
                      {totalUser} {totalUser === 1 ? "lead" : "leads"}
                    </div>
                  </div>
                  <span
                    style={{
                      background: "#f0fdf4",
                      color: "#16a34a",
                      borderRadius: "20px",
                      padding: "3px 12px",
                      fontSize: "13px",
                      fontWeight: 700,
                    }}
                  >
                    {totalUser}
                  </span>
                  {isDeleteAll.length > 0 && (
                    <span
                      className="sel-badge"
                      style={{
                        background: "linear-gradient(135deg,#ef4444,#dc2626)",
                        color: "white",
                        borderRadius: "20px",
                        padding: "3px 12px",
                        fontSize: "13px",
                        fontWeight: 700,
                      }}
                    >
                      {isDeleteAll.length} selected
                    </span>
                  )}
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  {/* View toggle — the board groups converted leads by where
                      their subscription stands, the list stays the detail view. */}
                  <div style={{ display: "flex", border: "1.5px solid #bbf7d0", borderRadius: 10, overflow: "hidden" }}>
                    {[
                      { key: "list", label: "List" },
                      { key: "kanban", label: "Kanban" },
                    ].map((v) => (
                      <button
                        key={v.key}
                        onClick={() => {
                          setViewMode(v.key);
                          setPageApi(1);   // same page-size mismatch as the Inbox
                          if (v.key === "kanban") {
                            // Ticking happens on rows, not cards.
                            setShowCheckboxes(false);
                            setIsDeleteAll([]);
                            setSelectAll(false);
                          }
                        }}
                        style={{
                          padding: "7px 16px", border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer",
                          background: viewMode === v.key ? "#16a34a" : "#fff",
                          color: viewMode === v.key ? "#fff" : "#16a34a",
                        }}
                      >
                        {v.label}
                      </button>
                    ))}
                  </div>

                  {/* No delete controls here: every lead on this screen is a live
                      customer with a subscription. */}
                </div>
              </div>

              {/* Table */}
              {viewMode === "list" ? (
              <div className="table-responsive">
                <table style={{ width: "100%", minWidth: 1100, borderCollapse: "collapse" }}>
                  <thead>
                    <tr
                      style={{
                        background: "linear-gradient(135deg,#f0fdf4,#dcfce7)",
                        borderBottom: "2px solid #bbf7d0",
                      }}
                    >
                      {showCheckboxes && (
                        <th
                          style={{
                            padding: "14px 16px",
                            textAlign: "center",
                          }}
                        >
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleCheckAll}
                            style={{ accentColor: "#16a34a" }}
                          />
                        </th>
                      )}
                      {[
                        "Name",
                        "Email",
                        "Mobile",
                        ...(showAttorneyCol ? ["Attorney No."] : []),
                        // "UTM Sources",
                        // "Sources",
                        "Source",
                        "Message",
                        "Date",
                        "Subscription",
                        "Status",
                        "Assigned To",
                      ].map((h) => (
                        <th
                          key={h}
                          style={{
                            padding: "14px 16px",
                            textAlign: "center",
                            fontSize: "12px",
                            fontWeight: 700,
                            color: "#16a34a",
                            textTransform: "uppercase",
                            letterSpacing: "0.6px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <TableLoader />
                    ) : listPageData.length > 0 ? (
                      listPageData.map((item, idx) => {
                        const isSel = isDeleteAll.includes(item.id);
                        const rowBg = isSel
                          ? "#f0fdf4"
                          : idx % 2 === 0
                            ? "white"
                            : "#f8fdf8";
                        return (
                          <tr
                            key={item.id}
                            onClick={(e) => {
                              // Checkboxes and action buttons keep their own jobs.
                              if (e.target.closest("button, input, a")) return;
                              openConverted(item);
                            }}
                            style={{
                              borderBottom: "1px solid #f5f5f5",
                              background: rowBg,
                              transition: "background 0.15s",
                              cursor: "pointer",
                            }}
                            onMouseEnter={(e) => {
                              if (!isSel)
                                e.currentTarget.style.background = "#f0fdf4";
                            }}
                            onMouseLeave={(e) => {
                              if (!isSel)
                                e.currentTarget.style.background = rowBg;
                            }}
                          >
                            {showCheckboxes && (
                              <td
                                style={{
                                  padding: "14px 16px",
                                  textAlign: "center",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSel}
                                  onChange={() => handleCheckboxChange(item)}
                                  style={{
                                    accentColor: "#16a34a",
                                    width: "15px",
                                    height: "15px",
                                  }}
                                />
                              </td>
                            )}

                            {/* Name */}
                            <td
                              style={{
                                padding: "14px 16px",
                                textAlign: "left",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "10px",
                                }}
                              >
                                <div
                                  style={{
                                    width: "34px",
                                    height: "34px",
                                    borderRadius: "50%",
                                    background:
                                      "linear-gradient(135deg,#16a34a,#15803d)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "white",
                                    fontSize: "13px",
                                    fontWeight: 700,
                                    flexShrink: 0,
                                  }}
                                >
                                  {item.name
                                    ?.split(" ")
                                    .map((w) => w.charAt(0))
                                    .join("")
                                    .slice(0, 2)
                                    .toUpperCase() || "?"}
                                </div>
                                <div>
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      color: "#1f2937",
                                      fontSize: "14px",
                                    }}
                                  >
                                    {item?.name}
                                  </span>
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                      marginTop: "2px",
                                    }}
                                  >
                                    <CheckCircleOutlined
                                      style={{
                                        color: "#16a34a",
                                        fontSize: "11px",
                                      }}
                                    />
                                    <span
                                      style={{
                                        fontSize: "11px",
                                        color: "#16a34a",
                                        fontWeight: 600,
                                      }}
                                    >
                                      Converted
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* Email */}
                            <td
                              style={{
                                padding: "14px 16px",
                                fontSize: "13px",
                                color: "#4b5563",
                              }}
                            >
                              {item?.email ? (
                                <span
                                  style={{
                                    background: "#eff6ff",
                                    color: "#2563eb",
                                    borderRadius: "4px",
                                    padding: "2px 8px",
                                    fontSize: "12px",
                                    display: "inline-block",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {item.email}
                                </span>
                              ) : (
                                <span
                                  style={{
                                    color: "#9ca3af",
                                    fontSize: "12px",
                                  }}
                                >
                                  N/A
                                </span>
                              )}
                            </td>

                            {/* Mobile */}
                            <td
                              style={{
                                padding: "14px 16px",
                                fontSize: "13px",
                                color: "#4b5563",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {item?.phone ? (
                                <span
                                  style={{
                                    fontWeight: 500,
                                    color: "#374151",
                                  }}
                                >
                                  {withCountryCode(item.country_code, item.phone)}
                                </span>
                              ) : (
                                <span
                                  style={{
                                    color: "#9ca3af",
                                    fontSize: "12px",
                                  }}
                                >
                                  N/A
                                </span>
                              )}
                              {/* Alternate number, only when present */}
                              <AdditionalPhones
                                value={item?.additional_phone}
                                dot
                                max={2}
                                style={{ marginTop: "3px" }}
                                itemStyle={{ fontWeight: 500, fontSize: "12px" }}
                              />
                            </td>

                            {/* Attorney No. — rendered only when the page has any */}
                            {showAttorneyCol && (
                              <td
                                style={{
                                  padding: "14px 16px",
                                  textAlign: "center",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {String(item?.attorney_number ?? "").trim() ? (
                                  <span
                                    style={{
                                      background: "#f5f3ff",
                                      color: "#7c3aed",
                                      border: "1px solid #ddd6fe",
                                      borderRadius: "6px",
                                      padding: "2px 9px",
                                      fontSize: "12px",
                                      fontWeight: 600,
                                      display: "inline-block",
                                    }}
                                  >
                                    {item.attorney_number}
                                  </span>
                                ) : (
                                  <span
                                    style={{
                                      color: "#9ca3af",
                                      fontSize: "12px",
                                    }}
                                  >
                                    —
                                  </span>
                                )}
                              </td>
                            )}

                            {/* UTM Sources */}
                            {/* <td
                              style={{
                                padding: "14px 16px",
                                fontSize: "13px",
                                color: "#4b5563",
                              }}
                            >
                              <div style={{ marginBottom: "2px" }}>
                                <span
                                  style={{
                                    color: "#9ca3af",
                                    fontSize: "12px",
                                  }}
                                >
                                  Source:{" "}
                                </span>
                                {item?.utm_source || "N/A"}
                              </div>
                              <div style={{ marginBottom: "2px" }}>
                                <span
                                  style={{
                                    color: "#9ca3af",
                                    fontSize: "12px",
                                  }}
                                >
                                  Medium:{" "}
                                </span>
                                {item?.utm_medium || "N/A"}
                              </div>
                              <div>
                                <span
                                  style={{
                                    color: "#9ca3af",
                                    fontSize: "12px",
                                  }}
                                >
                                  Campaign:{" "}
                                </span>
                                {item?.utm_campaign
                                  ? item.utm_campaign.substring(0, 10) + "..."
                                  : "N/A"}
                              </div>
                            </td> */}

                            {/* Sources */}
                            {/* <td
                              style={{
                                padding: "14px 16px",
                                fontSize: "13px",
                                color: "#4b5563",
                              }}
                            >
                              <div style={{ marginBottom: "2px" }}>
                                <span
                                  style={{
                                    color: "#9ca3af",
                                    fontSize: "12px",
                                  }}
                                >
                                  IP:{" "}
                                </span>
                                {item.system_ip ? (
                                  <a
                                    href={`https://ip-api.com/#${item.system_ip}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ color: "#16a34a" }}
                                  >
                                    {item.system_ip}
                                  </a>
                                ) : (
                                  "N/A"
                                )}
                              </div>
                              <div>
                                <span
                                  style={{
                                    color: "#9ca3af",
                                    fontSize: "12px",
                                  }}
                                >
                                  URL:{" "}
                                </span>
                                {item.source_url ? (
                                  <a
                                    href={item.source_url}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <span
                                      style={{
                                        background: "#f0fdf4",
                                        color: "#16a34a",
                                        borderRadius: "4px",
                                        padding: "1px 6px",
                                        fontSize: "12px",
                                      }}
                                    >
                                      Click Here
                                    </span>
                                  </a>
                                ) : (
                                  "N/A"
                                )}
                              </div>
                            </td> */}

                            {/* Source — the attribution that came with the lead
                                and travelled to the customer on conversion. */}
                            <td style={{ padding: "14px 16px", textAlign: "center", whiteSpace: "nowrap" }}>
                              {item.utm_source ? (
                                <span style={{ background: "#f5f3ff", color: "#7c3aed", border: "1px solid #ddd6fe", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600 }}>
                                  {item.utm_source}
                                </span>
                              ) : (
                                <span style={{ fontSize: 13, color: "#9ca3af" }}>—</span>
                              )}
                            </td>

                            {/* Message */}
                            <td style={{ padding: "14px 16px", fontSize: "13px", color: "#4b5563", whiteSpace: "nowrap" }}>
                              {item.message ? (
                                <Popover trigger="hover" title="Message" overlayStyle={{ maxWidth: 360 }} content={<div style={{ maxWidth: 320, maxHeight: 280, overflowY: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 13, lineHeight: 1.6 }}>{item.message}</div>}>
                                  <span style={{ cursor: "pointer", color: "#374151" }}>{item.message.length > 50 ? item.message.slice(0, 50) + "…" : item.message}</span>
                                </Popover>
                              ) : "N/A"}
                            </td>

                            {/* Date */}
                            <td
                              style={{
                                padding: "14px 16px",
                                fontSize: "13px",
                                color: "#4b5563",
                                whiteSpace: "nowrap",
                                textAlign: "center",
                              }}
                            >
                              {item.created_date ||
                                moment(item.created_at).format("DD MMM YYYY")}
                            </td>

                            {/* Subscription — a lead only lands here once one
                                exists, so show what was actually sold. */}
                            <td style={{ padding: "14px 16px", textAlign: "center", whiteSpace: "nowrap" }}>
                              {item.subscription ? (
                                <div style={{ display: "inline-flex", flexDirection: "column", gap: "3px", alignItems: "center" }}>
                                  <span style={{ fontWeight: 700, fontSize: "13px", color: "#111827" }}>
                                    {item.subscription.plan_name || "—"}
                                    {item.subscription.plan_type === "free_trial" && (
                                      <span style={{ marginLeft: 6, background: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe", borderRadius: 20, padding: "0 7px", fontSize: 10, fontWeight: 700 }}>
                                        Trial
                                      </span>
                                    )}
                                  </span>
                                  <span style={{ fontSize: "11px", color: "#6b7280" }}>
                                    {item.subscription.start_date
                                      ? moment(item.subscription.start_date).format("DD MMM YYYY")
                                      : "—"}
                                    {" → "}
                                    {item.subscription.end_date
                                      ? moment(item.subscription.end_date).format("DD MMM YYYY")
                                      : "—"}
                                  </span>
                                  <div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
                                    <span style={{ fontSize: "11px", fontWeight: 700, color: "#111827" }}>
                                      ₹{Number(item.subscription.paid_amount ?? item.subscription.plan_price ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                    {item.subscription.payment_status && (
                                      <span style={{ background: "#f5f5f5", color: "#4b5563", borderRadius: 20, padding: "0 8px", fontSize: 10, fontWeight: 700, textTransform: "capitalize" }}>
                                        {item.subscription.payment_status}
                                      </span>
                                    )}
                                    {item.subscription.subscription_status && (
                                      <span
                                        style={{
                                          borderRadius: 20, padding: "0 8px", fontSize: 10, fontWeight: 700, textTransform: "capitalize",
                                          background: item.subscription.subscription_status === "active" ? "#f0fdf4" : "#fef2f2",
                                          color: item.subscription.subscription_status === "active" ? "#16a34a" : "#dc2626",
                                          border: `1px solid ${item.subscription.subscription_status === "active" ? "#bbf7d0" : "#fecaca"}`,
                                        }}
                                      >
                                        {item.subscription.subscription_status}
                                      </span>
                                    )}
                                  </div>
                                  {item.subscription.order_id && (
                                    <span style={{ fontSize: "10px", color: "#9ca3af" }}>{item.subscription.order_id}</span>
                                  )}
                                </div>
                              ) : (
                                <span style={{ fontSize: "12px", color: "#9ca3af", fontStyle: "italic" }}>
                                  No subscription
                                </span>
                              )}
                            </td>

                            {/* Status */}
                            <td
                              style={{
                                padding: "14px 16px",
                                textAlign: "center",
                              }}
                            >
                              {(() => {
                                /* Status = where the SUBSCRIPTION stands, not the
                                   lead's follow-up status: every lead here is
                                   converted, so "Converted" said nothing useful.
                                   Same buckets and colours as the Kanban board. */
                                const bucket = classifyConverted(item);
                                const col =
                                  DONE_COLUMNS.find((c) => c.key === bucket) ||
                                  DONE_COLUMNS[DONE_COLUMNS.length - 1];
                                return (
                                  <span
                                    style={{
                                      background: col.bg,
                                      color: col.color,
                                      border: `1px solid ${col.border}`,
                                      borderRadius: "20px",
                                      padding: "3px 12px",
                                      fontSize: "12px",
                                      fontWeight: 600,
                                      whiteSpace: "nowrap",
                                      display: "inline-block",
                                    }}
                                  >
                                    {col.label}
                                  </span>
                                );
                              })()}
                            </td>

                            {/* Assigned To */}
                            <td
                              style={{
                                padding: "14px 16px",
                                textAlign: "center",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {item?.assign_to_name ? (
                                <span
                                  style={{
                                    background: "#f0fdf4",
                                    color: "#16a34a",
                                    border: "1px solid #bbf7d0",
                                    borderRadius: "20px",
                                    padding: "3px 12px",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {item.assign_to_name}
                                </span>
                              ) : (
                                <span
                                  style={{
                                    background: "#f5f5f5",
                                    color: "#9ca3af",
                                    borderRadius: "20px",
                                    padding: "3px 12px",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    fontStyle: "italic",
                                  }}
                                >
                                  Unassigned
                                </span>
                              )}
                            </td>

                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan={showCheckboxes ? 11 : 10}
                          style={{
                            padding: "60px 20px",
                            textAlign: "center",
                          }}
                        >
                          <div style={{ color: "#9ca3af" }}>
                            <CheckCircleOutlined
                              style={{
                                fontSize: "48px",
                                marginBottom: "12px",
                                display: "block",
                              }}
                            />
                            <div
                              style={{
                                fontSize: "16px",
                                fontWeight: 600,
                                color: "#6b7280",
                              }}
                            >
                              No Converted Leads Yet
                            </div>
                            <div
                              style={{
                                fontSize: "13px",
                                color: "#9ca3af",
                                marginTop: "4px",
                              }}
                            >
                              Leads marked as converted will appear here
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              ) : (
                /* KANBAN - grouped by where the subscription stands */
                <div style={{ padding: "20px 24px", overflowX: "auto" }}>
                  {isLoading ? (
                    <TableLoader />
                  ) : listPageData.length === 0 ? (
                    <div style={{ padding: "50px 0", textAlign: "center", color: "#9ca3af" }}>
                      No converted leads yet.
                    </div>
                  ) : (
                    <div style={{ display: "flex", gap: 16, alignItems: "flex-start", minWidth: "fit-content" }}>
                      {doneColumns.map((col) => (
                        <div key={col.key} style={{ flex: "0 0 300px", background: "#f8fafc", borderRadius: 14, border: "1px solid " + col.border, overflow: "hidden" }}>
                          <div style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 8, borderBottom: "2px solid " + col.border, background: "#fff" }}>
                            <span style={{ background: col.bg, color: col.color, border: "1px solid " + col.border, borderRadius: 20, padding: "3px 12px", fontSize: 12, fontWeight: 700 }}>
                              {col.label}
                            </span>
                            <span style={{ background: col.color, color: "#fff", borderRadius: "50%", width: 20, height: 20, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700 }}>
                              {col.count}
                            </span>
                          </div>

                          <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 10, maxHeight: "62vh", overflowY: "auto" }}>
                            {col.items.length === 0 ? (
                              <div style={{ padding: "24px 0", textAlign: "center", color: "#cbd5e1", fontSize: 12 }}>No leads</div>
                            ) : col.items.map((item) => {
                              const sub = item.subscription;
                              const end = sub && sub.end_date ? moment(sub.end_date, "YYYY-MM-DD") : null;
                              const daysLeft =
                                sub && sub.days_to_expiry != null
                                  ? Number(sub.days_to_expiry)
                                  : end && end.isValid()
                                    ? end.diff(moment().startOf("day"), "days")
                                    : null;
                              return (
                                <div
                                  key={item.id}
                                  onClick={() => openConverted(item)}
                                  style={{ background: "#fff", borderRadius: 12, border: "1px solid #eef2f7", padding: 12, cursor: "pointer", boxShadow: "0 1px 4px rgba(0,0,0,.04)" }}
                                >
                                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                                    <div style={{ width: 34, height: 34, borderRadius: "50%", background: "linear-gradient(135deg,#16a34a,#15803d)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                                      {(item.name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
                                    </div>
                                    <div style={{ minWidth: 0 }}>
                                      <div style={{ fontWeight: 700, fontSize: 13, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {item.name || "-"}
                                      </div>
                                      {item.company && (
                                        <div style={{ fontSize: 11, color: "#9ca3af", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.company}</div>
                                      )}
                                    </div>
                                  </div>

                                  <div style={{ fontSize: 12, color: "#6b7280", display: "flex", flexDirection: "column", gap: 3, marginBottom: 8 }}>
                                    {item.email && <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.email}</div>}
                                    {item.phone && <div style={{ fontWeight: 600, color: "#374151" }}>{withCountryCode(item.country_code, item.phone)}</div>}
                                    <AdditionalPhones value={item.additional_phone} max={2} />
                                    {item.utm_source && (
                                      <div style={{ color: "#7c3aed", fontWeight: 600, fontSize: 11 }}>
                                        🌐 {item.utm_source}
                                      </div>
                                    )}
                                  </div>

                                  {sub ? (
                                    <div style={{ paddingTop: 8, borderTop: "1px solid #f3f4f6", display: "flex", flexDirection: "column", gap: 4 }}>
                                      <div style={{ fontSize: 12, fontWeight: 700, color: "#111827" }}>
                                        {sub.plan_name || "-"}
                                        <span style={{ marginLeft: 6, fontWeight: 700, color: "#16a34a" }}>
                                          {"₹" + Number(sub.paid_amount != null ? sub.paid_amount : (sub.plan_price || 0)).toLocaleString("en-IN")}
                                        </span>
                                      </div>
                                      <div style={{ fontSize: 11, color: "#6b7280" }}>
                                        {sub.start_date ? moment(sub.start_date).format("DD MMM YYYY") : "-"}
                                        {" → "}
                                        {sub.end_date ? moment(sub.end_date).format("DD MMM YYYY") : "-"}
                                      </div>
                                      <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                                        {daysLeft !== null && (
                                          <span style={{ background: col.bg, color: col.color, border: "1px solid " + col.border, borderRadius: 20, padding: "1px 8px", fontSize: 10, fontWeight: 700 }}>
                                            {daysLeft < 0 ? "Expired " + Math.abs(daysLeft) + "d ago" : daysLeft === 0 ? "Expires today" : daysLeft + "d left"}
                                          </span>
                                        )}
                                        {sub.payment_status && (
                                          <span style={{ background: "#f5f5f5", color: "#4b5563", borderRadius: 20, padding: "1px 8px", fontSize: 10, fontWeight: 700, textTransform: "capitalize" }}>
                                            {sub.payment_status}
                                          </span>
                                        )}
                                      </div>
                                      {sub.order_id && (
                                        <div style={{ fontSize: 10, color: "#9ca3af" }}>{sub.order_id}</div>
                                      )}
                                    </div>
                                  ) : (
                                    <div style={{ paddingTop: 8, borderTop: "1px solid #f3f4f6", fontSize: 11, color: "#9ca3af", fontStyle: "italic" }}>
                                      No subscription on this lead
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DoneLeads;
